(() => {
  const instances = new Map();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = matchMedia('(min-width: 1000px)');
  let stickyOffset = -1;
  let header, announcement;
  const headerObserver = window.ResizeObserver ? new ResizeObserver(measureHeader) : null;
  function measureHeader() {
    const nextHeader = document.querySelector('.header')?.closest('[id^="shopify-section-"]');
    const nextAnnouncement = document.querySelector('.announcement-bar')?.closest('[id^="shopify-section-"]');
    if (nextHeader !== header) {
      if (header) headerObserver?.unobserve(header);
      header = nextHeader;
      if (header) headerObserver?.observe(header);
    }
    if (nextAnnouncement !== announcement) {
      if (announcement) headerObserver?.unobserve(announcement);
      announcement = nextAnnouncement;
      if (announcement) headerObserver?.observe(announcement);
    }
    const height = [header, announcement].reduce((sum, node) => {
      if (!node) return sum;
      const position = getComputedStyle(node).position;
      return sum + (position === 'sticky' || position === 'fixed' ? node.getBoundingClientRect().height : 0);
    }, 0);
    const next = Math.ceil(height);
    if (next === stickyOffset) return;
    stickyOffset = next;
    document.documentElement.style.setProperty('--sv-sticky-offset', `${stickyOffset}px`);
    document.dispatchEvent(new Event('sv:header-change'));
  }
  measureHeader();
  window.addEventListener('resize', measureHeader, { passive: true });
  function mountLookbook(root) {
    if (instances.has(root)) return;
    const scenes = [...root.querySelectorAll('[data-look-scene]')];
    const controls = [...root.querySelectorAll('[data-look-index]')];
    const track = root.querySelector('.sv-lookbook-track');
    const stage = root.querySelector('.sv-lookbook-stage');
    const rail = root.querySelector('.sv-lookbook-scenes');
    if (!scenes.length || !track || !stage || !rail) return;
    let enhanced = false, frame = 0, active = -1, distance = 1, alive = true;
    function select(index) {
      active = index;
      controls.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
    }
    function clear() {
      scenes.forEach(scene => {
        scene.style.removeProperty('clip-path');
        scene.style.removeProperty('transform');
        scene.style.removeProperty('opacity');
        scene.style.removeProperty('z-index');
        scene.style.removeProperty('--sv-look-zoom');
        scene.inert = false;
        scene.removeAttribute('aria-hidden');
      });
    }
    function paint() {
      frame = 0;
      if (!enhanced) return;
      const raw = Math.max(0, Math.min(scenes.length - 1, (stickyOffset - track.getBoundingClientRect().top) / distance));
      const whole = Math.floor(raw), fraction = raw - whole;
      const t = Math.max(0, Math.min(1, (fraction - .18) / .64));
      const progress = whole + t * t * (3 - 2 * t);
      const nearest = Math.min(scenes.length - 1, Math.round(progress));
      scenes.forEach((scene, i) => {
        const delta = i - progress;
        scene.style.clipPath = `inset(${Math.max(0, Math.min(100, delta * 100))}% 0 0 0)`;
        scene.style.transform = `scale(${delta < 0 ? Math.max(.94, 1 + delta * .035) : 1})`;
        scene.style.opacity = String(delta < 0 ? Math.max(.8, 1 + delta * .12) : 1);
        scene.style.zIndex = String(i + 1);
        scene.style.setProperty('--sv-look-zoom', String(1 + Math.min(1, Math.abs(delta)) * .025));
        scene.inert = i !== nearest;
        scene.setAttribute('aria-hidden', String(i !== nearest));
      });
      if (active !== nearest) select(nearest);
    }
    function schedule() { if (enhanced && !frame) frame = requestAnimationFrame(paint); }
    function setup() {
      if (!alive) return;
      clear();
      root.removeAttribute('data-enhanced');
      const stageHeight = Math.min(860, window.innerHeight - stickyOffset - 16);
      enhanced = desktop.matches && stageHeight >= 570 && !reduced.matches && root.dataset.motion === 'true' && !window.Shopify?.designMode && scenes.length > 1;
      if (enhanced) {
        distance = window.innerHeight * (parseFloat(getComputedStyle(root).getPropertyValue('--look-scroll')) || 60) / 100;
        root.style.setProperty('--sv-stage-height', `${stageHeight}px`);
        root.style.setProperty('--sv-track-height', `${stageHeight + distance * (scenes.length - 1)}px`);
        root.setAttribute('data-enhanced', '');
        if (scenes.some(scene => scene.scrollHeight > scene.clientHeight + 2)) {
          enhanced = false;
          root.removeAttribute('data-enhanced');
        }
      }
      if (enhanced) paint(); else { clear(); railScroll(); }
    }
    function railScroll() {
      if (enhanced) return;
      const current = scenes.reduce((best, scene, i) => {
        const offset = Math.abs(scene.offsetLeft - scenes[0].offsetLeft - rail.scrollLeft);
        return offset < best.distance ? { distance: offset, index: i } : best;
      }, { distance: Infinity, index: 0 });
      select(current.index);
    }
    function moveTo(index) {
      if (!scenes[index]) return;
      if (enhanced) {
        const top = window.scrollY + track.getBoundingClientRect().top - stickyOffset + distance * index;
        window.scrollTo({ top, behavior: reduced.matches ? 'instant' : 'smooth' });
      } else if (!desktop.matches) {
        rail.scrollTo({ left: scenes[index].offsetLeft - scenes[0].offsetLeft, behavior: reduced.matches ? 'instant' : 'smooth' });
        select(index);
      } else scenes[index].scrollIntoView({ block: 'center', behavior: 'instant' });
    }
    function click(event) {
      const button = event.target.closest('[data-look-index]');
      if (button && root.contains(button)) moveTo(Number(button.dataset.lookIndex));
    }
    function blockSelect(event) {
      const index = scenes.findIndex(scene => scene === event.target || scene.contains(event.target));
      if (index >= 0) moveTo(index);
    }
    root.addEventListener('click', click);
    root.addEventListener('shopify:block:select', blockSelect);
    rail.addEventListener('scroll', railScroll, { passive: true });
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', setup, { passive: true });
    document.addEventListener('sv:header-change', setup);
    reduced.addEventListener('change', setup);
    desktop.addEventListener('change', setup);
    setup();
    document.fonts?.ready.then(setup);
    instances.set(root, () => {
      alive = false;
      cancelAnimationFrame(frame);
      root.removeEventListener('click', click);
      root.removeEventListener('shopify:block:select', blockSelect);
      rail.removeEventListener('scroll', railScroll);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', setup);
      document.removeEventListener('sv:header-change', setup);
      reduced.removeEventListener('change', setup);
      desktop.removeEventListener('change', setup);
      root.removeAttribute('data-enhanced');
      clear();
    });
  }
  function mountCampaign(root) {
    if (instances.has(root)) return;
    let frame = 0, visible = true;
    function paint() {
      frame = 0;
      if (!visible || reduced.matches || !desktop.matches || root.dataset.motion !== 'true' || window.Shopify?.designMode) {
        root.style.removeProperty('--sv-hero-shift');
        root.style.removeProperty('--sv-hero-scale');
        return;
      }
      const rect = root.getBoundingClientRect();
      const p = Math.max(0, Math.min(1, -rect.top / rect.height));
      root.style.setProperty('--sv-hero-shift', `${p * 34}px`);
      root.style.setProperty('--sv-hero-scale', String(1 + p * .06));
    }
    function schedule() { if (!frame) frame = requestAnimationFrame(paint); }
    function enter() {
      root.classList.toggle('sv-entered', !reduced.matches && root.dataset.motion === 'true' && !window.Shopify?.designMode);
      schedule();
    }
    const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; schedule(); });
    observer.observe(root);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    reduced.addEventListener('change', enter);
    enter();
    instances.set(root, () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      reduced.removeEventListener('change', enter);
    });
  }
  function scan(scope = document) {
    scope.querySelectorAll('[data-sv-lookbook]').forEach(mountLookbook);
    scope.querySelectorAll('[data-sv-campaign]').forEach(mountCampaign);
  }
  scan();
  document.addEventListener('shopify:section:load', event => { measureHeader(); scan(event.target); });
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, cleanup] of instances) if (event.target.contains(root)) { cleanup(); instances.delete(root); }
  });
})();
